import { Fragment } from "react";
import classNames from "classnames";
import { GreentickGreenCircle } from "components/icons/GreentickGreenCircle";
import { DottedCircle } from "components/icons/DottedCircle";
import { isEntityComplete } from "v2/utils/taxSetupProgress";

export type EntityRef = { id: string; name: string };

type Orientation = "vertical" | "horizontal";

type EntitySetupChecklistProps = {
  entities: EntityRef[];
  progressById: Record<string, number>;
  orientation?: Orientation;
};

export const EntityStatusIcon = ({ isComplete }: { isComplete: boolean }) => {
  if (isComplete) {
    return (
      <GreentickGreenCircle
        width="14"
        height="14"
        className="t-shrink-0 t-text-dark_green"
      />
    );
  }

  return <DottedCircle size="14" className="t-shrink-0 t-text-purple-30" />;
};

const HorizontalItem = ({
  entity,
  isComplete,
}: {
  entity: EntityRef;
  isComplete: boolean;
}) => (
  <div className="t-flex t-items-center t-gap-1 after:t-inline-block after:t-h-px after:t-w-4 after:t-bg-text-30/50 after:t-content-[''] last:after:t-hidden">
    <EntityStatusIcon isComplete={isComplete} />
    <span className="t-text-body-sm t-text-text-30">{entity.name}</span>
  </div>
);

const VerticalItem = ({
  entity,
  isComplete,
  isLast,
}: {
  entity: EntityRef;
  isComplete: boolean;
  isLast: boolean;
}) => (
  <>
    <div className="t-flex t-items-center t-gap-2">
      <span className="t-flex t-w-4 t-items-center t-justify-center">
        <EntityStatusIcon isComplete={isComplete} />
      </span>
      <span className="t-flex-1 t-text-body-sm t-text-text-60">
        {entity.name}
      </span>
    </div>

    {!isLast && (
      <span className="t-flex t-w-4 t-justify-center">
        <span className="t-h-3 t-w-px t-bg-neutral-10" />
      </span>
    )}
  </>
);

export const EntitySetupChecklist = ({
  entities,
  progressById,
  orientation = "vertical",
}: EntitySetupChecklistProps) => {
  const isHorizontal = orientation === "horizontal";

  return (
    <div
      className={classNames("t-flex", {
        "t-flex-col t-gap-[3px]": !isHorizontal,
        "t-items-center t-gap-1": isHorizontal,
      })}
    >
      {entities.map((entity, index) => {
        const isComplete = isEntityComplete(progressById[entity.id] ?? 0);

        if (isHorizontal) {
          return (
            <HorizontalItem
              key={entity.id}
              entity={entity}
              isComplete={isComplete}
            />
          );
        }

        return (
          <Fragment key={entity.id}>
            <VerticalItem
              entity={entity}
              isComplete={isComplete}
              isLast={index === entities.length - 1}
            />
          </Fragment>
        );
      })}
    </div>
  );
};
